import React, { useState } from "react";
import { CoffeeBagItem } from "./CoffeeBagItem";
import { ItemView } from "../ItemView";
import { useCart } from "../../../../contexts/CartContext";
import { ICONS } from "../../../../util/constants";
import "./CoffeeBagDetail.css";
import { ItemType } from "../ItemModel";
import { allowsUnlimitedPurchases } from "../../../../util/limitedTimeOffer";
import {
  SUBSCRIPTION_CADENCES,
  SubscriptionCadence,
  SubscriptionPlan,
} from "../../../../models/AccountModel";
import { useAccount } from "../../../../contexts/AccountContext";

interface CoffeeBagDetailProps {
  item: CoffeeBagItem;
  onBack: () => void;
}

const CoffeeBagDetail: React.FC<CoffeeBagDetailProps> = ({ item, onBack }) => {
  const { cart, forceUpdate, showToast } = useCart();
  const { account } = useAccount();
  const isPartnerAccount =
    account?.label === "wholesale" || account?.label === "church-ministry";
  const isPartnerVariant = (
    variant: NonNullable<CoffeeBagItem["variants"]>[number],
  ): boolean =>
    isPartnerAccount
      ? variant.isWholesale === true ||
        variant.sku.toUpperCase().endsWith("-WS") ||
        (account?.label === "wholesale" &&
          ["200g", "1lb", "3lb", "5lb"].includes(
            (variant.weight || "").replace(/\s/g, "").toLowerCase(),
          ))
      : !variant.isWholesale && !variant.sku.endsWith("-WS");
  // Derive available weights from variants if they exist, otherwise use parent's weights
  const availableWeights =
    item.variants && item.variants.length > 0
      ? item.variants
          .filter(isPartnerVariant)
          .map((v) => v.weight)
          .filter((w): w is string => w !== "")
          .filter((weight, index, self) => self.indexOf(weight) === index)
          .reverse()
      : item.weights || [];
  const [selectedWeight, setSelectedWeight] = useState<string>(
    availableWeights[0] || "200g",
  );
  const [quantity, setQuantity] = useState<number>(1);
  const [openBrewMethods, setOpenBrewMethods] = useState<string[]>([]);
  const [purchaseMode, setPurchaseMode] = useState<"one-time" | "subscription">(
    "one-time",
  );
  const [subscriptionFrequency, setSubscriptionFrequency] =
    useState<SubscriptionCadence>("every-session");
  const selectedVariant = item.variants
    ?.filter((v) => v.weight === selectedWeight && isPartnerVariant(v))
    .sort(
      (first, second) =>
        Number(
          !!(second.isWholesale || second.sku.toUpperCase().endsWith("-WS")),
        ) -
        Number(
          !!(first.isWholesale || first.sku.toUpperCase().endsWith("-WS")),
        ),
    )[0];

  const subscriptionPlan: SubscriptionPlan =
    `${quantity === 2 ? "two-bags" : "one-bag"}-${subscriptionFrequency}` as SubscriptionPlan;

  const handleAddToCart = () => {
    // Find the matching variant for the selected weight
    let variantSku: string | undefined = undefined;
    let variantPrice = item.price;
    let variantShippingWeight: number | undefined = undefined;

    if (item.variants && item.variants.length > 0 && selectedWeight) {
      const variant = selectedVariant;
      if (variant) {
        variantSku = variant.sku;
        variantPrice = variant.price > 0 ? variant.price : item.price;
        variantShippingWeight = variant.shippingWeight;
      }
    }

    const unitPrice =
      purchaseMode === "subscription" ? variantPrice * 0.95 : variantPrice;
    const result = cart.addItem(item, quantity, {
      weight: selectedWeight,
      variantSku,
      variantPrice: unitPrice,
      variantShippingWeight,
      ...(isPartnerAccount ? { isPartnerOrder: true } : {}),
      ...(purchaseMode === "subscription" ? { subscriptionPlan } : {}),
    });
    forceUpdate();

    if (result.success) {
      showToast(result.message, "success");
    } else {
      showToast(result.message, "error");
    }
  };

  const calculatePrice = () => {
    // If variants exist, use variant price
    if (item.variants && item.variants.length > 0 && selectedWeight) {
      const variant = selectedVariant;
      if (variant && variant.price > 0) {
        return (
          variant.price *
          quantity *
          (purchaseMode === "subscription" ? 0.95 : 1)
        ).toFixed(2);
      }
    }

    // Use base price from Notion
    return (
      item.price *
      quantity *
      (purchaseMode === "subscription" ? 0.95 : 1)
    ).toFixed(2);
  };

  const getSelectedUnitPrice = (): number => {
    return selectedVariant && selectedVariant.price > 0
      ? selectedVariant.price
      : item.price;
  };

  const isSoldOut = () => {
    // If variants exist, check the selected variant's isSoldOut flag (unless LTO unlimited purchases is enabled)
    if (item.variants && item.variants.length > 0 && selectedWeight) {
      const variant = selectedVariant;
      if (variant) {
        return variant.isSoldOut === true && !allowsUnlimitedPurchases(item);
      }
    }

    // Fall back to overall item quantity (unless LTO unlimited purchases is enabled)
    return item.quantity === 0 && !allowsUnlimitedPurchases(item);
  };

  const renderMetadata = (coffeeItem: CoffeeBagItem) => (
    <>
      <span className="category-badge">{ItemType[coffeeItem.itemType]}</span>
    </>
  );

  const renderExtraInfo = (coffeeItem: CoffeeBagItem) => {
    const origins = coffeeItem.origins?.length
      ? coffeeItem.origins
      : coffeeItem.origin
        ? [coffeeItem.origin]
        : [];
    const sections = [
      {
        title: "Roast Level",
        values: coffeeItem.roastLevel ? [coffeeItem.roastLevel] : [],
      },
      { title: origins.length > 1 ? "Regions" : "Region", values: origins },
      { title: "Tasting Notes", values: coffeeItem.tastingNotes || [] },
    ].filter((section) => section.values.length > 0);

    if (sections.length === 0) return null;

    return (
      <div className="flavor-notes">
        {sections.map((section) => (
          <React.Fragment key={section.title}>
            <h3>{section.title}</h3>
            <div className="flavor-tags">
              {section.values.map((value, index) => (
                <span key={index} className="flavor-tag">
                  {value}
                </span>
              ))}
            </div>
          </React.Fragment>
        ))}
      </div>
    );
  };

  const renderOptions = () => (
    <>
      <div className="option-group">
        <label className="option-label">Weight</label>
        <div className="weight-options">
          {availableWeights.map((weight) => {
            const variant = item.variants?.find((v) => v.weight === weight);
            // Only show weight if it has an active variant
            if (!variant || variant.active === false) {
              return null;
            }
            return (
              <button
                key={weight}
                className={`weight-button ${selectedWeight === weight ? "selected" : ""}`}
                onClick={() => setSelectedWeight(weight)}
              >
                {weight}
              </button>
            );
          })}
        </div>
      </div>

      {!isPartnerAccount && (
        <fieldset className="subscription-options">
          <legend>Purchase option</legend>
          <div className="subscription-plan-options">
            <label className={purchaseMode === "one-time" ? "selected" : ""}>
              <input
                type="radio"
                name="purchase-mode"
                checked={purchaseMode === "one-time"}
                onChange={() => setPurchaseMode("one-time")}
              />
              <span>One-time purchase</span>
              <strong>${getSelectedUnitPrice().toFixed(2)}</strong>
            </label>
            <label
              className={purchaseMode === "subscription" ? "selected" : ""}
            >
              <input
                type="radio"
                name="purchase-mode"
                checked={purchaseMode === "subscription"}
                onChange={() => {
                  setPurchaseMode("subscription");
                  setQuantity((current) => Math.min(2, current));
                }}
              />
              <span>Subscribe &amp; save 5%</span>
              <strong>
                From ${(getSelectedUnitPrice() * 0.95).toFixed(2)}
              </strong>
            </label>
          </div>
          {purchaseMode === "subscription" && (
            <div className="subscription-frequency">
              <p>Select delivery frequency</p>
              <div className="subscription-plan-options">
                {SUBSCRIPTION_CADENCES.map((cadence) => (
                  <label
                    key={cadence.id}
                    className={
                      subscriptionFrequency === cadence.id ? "selected" : ""
                    }
                  >
                    <input
                      type="radio"
                      name="subscription-frequency"
                      checked={subscriptionFrequency === cadence.id}
                      onChange={() => setSubscriptionFrequency(cadence.id)}
                    />
                    <span>{cadence.label}</span>
                  </label>
                ))}
              </div>
              <p>
                You can skip, change frequency, order early, or cancel anytime
                from your account.
              </p>
            </div>
          )}
        </fieldset>
      )}

      <div className="option-group">
        <label className="option-label">Quantity</label>
        <div className="quantity-selector">
          <button
            className="quantity-button"
            onClick={() => setQuantity(Math.max(1, quantity - 1))}
          >
            −
          </button>
          <span className="quantity-display">{quantity}</span>
          <button
            className="quantity-button"
            onClick={() =>
              setQuantity(
                Math.min(
                  !isPartnerAccount && purchaseMode === "subscription" ? 2 : 99,
                  quantity + 1,
                ),
              )
            }
          >
            +
          </button>
        </div>
      </div>
    </>
  );

  const toggleBrewMethod = (name: string) =>
    setOpenBrewMethods((open) =>
      open.includes(name) ? open.filter((n) => n !== name) : [...open, name],
    );

  const renderBrewingMethod = () => {
    if (!item.brewingMethods) {
      return null;
    }

    const brewingMethods = [
      {
        name: "Single Dripper",
        icon: (
          <img src={ICONS.v60} alt="Single Dripper" className="brewing-icon" />
        ),
        coffee: item.brewingMethods.singleDripper?.dose || "",
        water: item.brewingMethods.singleDripper?.yield || "",
        waterTemperature:
          item.brewingMethods.singleDripper?.waterTemperature || "",
        ratio: item.brewingMethods.singleDripper?.ratio || "",
        time: item.brewingMethods.singleDripper?.time || "",
        description: item.brewingMethods.singleDripper?.description || "",
      },
      {
        name: "Batch Dripper",
        icon: (
          <img
            src={ICONS.chemex}
            alt="Batch Dripper"
            className="brewing-icon"
          />
        ),
        coffee: item.brewingMethods.batchDripper?.dose || "",
        water: item.brewingMethods.batchDripper?.yield || "",
        waterTemperature:
          item.brewingMethods.batchDripper?.waterTemperature || "",
        ratio: item.brewingMethods.batchDripper?.ratio || "",
        time: item.brewingMethods.batchDripper?.time || "",
        description: item.brewingMethods.batchDripper?.description || "",
      },
      {
        name: "Espresso",
        icon: (
          <img src={ICONS.espresso} alt="Espresso" className="brewing-icon" />
        ),
        coffee: item.brewingMethods.espresso?.dose || "",
        water: item.brewingMethods.espresso?.yield || "",
        waterTemperature: item.brewingMethods.espresso?.waterTemperature || "",
        ratio: item.brewingMethods.espresso?.ratio || "",
        maxPressure: item.brewingMethods.espresso?.maxPressure || "",
        time: item.brewingMethods.espresso?.time || "",
        description: item.brewingMethods.espresso?.description || "",
      },
      {
        name: "Milk Drink",
        icon: (
          <img src={ICONS.espresso} alt="Milk Drink" className="brewing-icon" />
        ),
        coffee: item.brewingMethods.milkDrink?.dose || "",
        water: item.brewingMethods.milkDrink?.yield || "",
        waterTemperature: item.brewingMethods.milkDrink?.waterTemperature || "",
        ratio: item.brewingMethods.milkDrink?.ratio || "",
        milkRatio: item.brewingMethods.milkDrink?.milkRatio || "",
        maxPressure: item.brewingMethods.milkDrink?.maxPressure || "",
        time: item.brewingMethods.milkDrink?.time || "",
        description: item.brewingMethods.milkDrink?.description || "",
      },
    ].filter(
      (method) =>
        method.coffee ||
        method.water ||
        method.ratio ||
        method.milkRatio ||
        method.maxPressure ||
        method.time ||
        method.description,
    );

    if (brewingMethods.length === 0) {
      return null;
    }

    return (
      <div className="coffee-brew-guide">
        <div className="coffee-brew-guide-heading">
          <p className="coffee-detail-eyebrow">BREW GUIDE</p>
          <h3>Make a good cup at home.</h3>
          <p>
            Don't know where to start? We got you! We&apos;ve included our
            personal brew guides and tasting experiences so you can find the
            version of {item.name} that feels most like yours.
          </p>
          <p>
            Everyone's taste and preference is different. These brew guides
            should be used as a starting point, not a strict rule. Play around
            with what tastes best to you, that's what we care about the most!
          </p>
        </div>
        <div className="coffee-brew-methods">
          {brewingMethods.map((method, index) => {
            const isOpen = openBrewMethods.includes(method.name);
            const parameters = [
              ["COFFEE", method.coffee],
              ["WATER", method.water],
              ["TEMPERATURE", method.waterTemperature],
              ["RATIO", method.ratio],
              ["TIME", method.time],
              ["MILK RATIO", method.milkRatio],
              ["MAX PRESSURE", method.maxPressure],
            ].filter(([, value]) => value);
            return (
              <div
                key={index}
                className={`coffee-brew-method${isOpen ? " coffee-brew-method--open" : ""}`}
              >
                <button
                  type="button"
                  className="coffee-brew-method-toggle"
                  aria-expanded={isOpen}
                  onClick={() => toggleBrewMethod(method.name)}
                >
                  <span className="coffee-brew-method-icon">{method.icon}</span>
                  <strong>
                    {method.name === "Single Dripper"
                      ? "Pour-over"
                      : method.name === "Batch Dripper"
                        ? "Batch brew"
                        : method.name === "Milk Drink"
                          ? "Milk drink"
                          : method.name}
                  </strong>
                  <span className="coffee-brew-chevron" aria-hidden="true" />
                </button>
                <div
                  className="coffee-brew-method-collapse"
                  aria-hidden={!isOpen}
                >
                  <div
                    className="coffee-brew-method-body"
                    {...(!isOpen ? { inert: "" } : {})}
                  >
                    {parameters.length > 0 && (
                      <dl className="coffee-brew-parameters">
                        {parameters.map(([label, value]) => (
                          <div key={label}>
                            <dt>{label}</dt>
                            <dd>{value}</dd>
                          </div>
                        ))}
                      </dl>
                    )}
                    {method.description && (
                      <p className="coffee-brew-description">
                        {method.description}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <ItemView
      item={item}
      onBack={onBack}
      renderMetadata={() => renderMetadata(item)}
      renderExtraInfo={() => renderExtraInfo(item)}
      renderOptions={renderOptions}
      renderBrewingMethod={renderBrewingMethod}
      calculatePrice={calculatePrice}
      handleAddToCart={handleAddToCart}
      isSoldOut={isSoldOut()}
    />
  );
};

export default CoffeeBagDetail;
